"use client";

import { memo, useState, useCallback } from "react";
import { useTranslations } from "next-intl";
import { Text, ScrollArea, Stack, Card, LoadingOverlay } from "@forestsync/ui";
import { type TokenListItem } from "@/types/glassroom";
import { TokenSkeleton } from "../LoadingSkeletons";
import { TokenGrid } from "./TokenGrid";
import { TokenControls } from "./TokenControls";

interface TokenListProps {
  tokens: TokenListItem[];
  isLoading?: boolean;
  emptyMessage?: string;
  maxHeight?: number;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export type { TokenListProps };

// Main TokenList Component
export const TokenList = memo(function TokenList({
  tokens,
  isLoading = false,
  emptyMessage,
  maxHeight = 400,
  currentPage,
  totalPages,
  onPageChange,
}: TokenListProps) {
  const t = useTranslations();
  const [internalLoading, setInternalLoading] = useState(false);
  const [pendingPage, setPendingPage] = useState<number | null>(null);

  // Handle page change with internal loading state
  const handlePageChange = useCallback(
    async (page: number) => {
      if (page === currentPage || internalLoading) return;

      setPendingPage(page);
      setInternalLoading(true);
      try {
        await onPageChange(page);
        setPendingPage(null);
      } finally {
        setInternalLoading(false);
      }
    },
    [currentPage, onPageChange, internalLoading],
  );

  // Show full skeleton only on initial load (no tokens yet)
  if (isLoading && tokens.length === 0) {
    return (
      <div className="w-full space-y-4">
        <TokenSkeleton count={8} />
      </div>
    );
  }

  if (!isLoading && tokens.length === 0) {
    return (
      <div className="w-full">
        <Text className="text-gray-500 dark:text-gray-400 text-center py-8">
          {emptyMessage || t("sink.noTokensIssued")}
        </Text>
      </div>
    );
  }

  // Use pending page for display during loading, actual page otherwise
  const displayPage = pendingPage || currentPage;
  const isPaginationLoading = internalLoading;

  return (
    <Stack gap="md" className="w-full">
      {/* Combined Controls (Legend + Pagination) */}
      <TokenControls
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={tokens.length}
        displayPage={displayPage}
        isPaginationLoading={isPaginationLoading}
        onPageChange={handlePageChange}
      />

      {/* Token Grid with Loading Overlay */}
      <Card withBorder padding={0} style={{ height: maxHeight }}>
        <ScrollArea h={maxHeight}>
          <div className="p-4">
            <LoadingOverlay visible={isPaginationLoading} />
            <TokenGrid
              tokens={tokens as TokenListItem[]}
              showSkeletons={isPaginationLoading} // Show skeletons during pagination loading
            />
          </div>
        </ScrollArea>
      </Card>
    </Stack>
  );
});
